import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootStates } from 'redux/store';
import { apiUrl } from '../../constants/paths';

export const inspectionApi = createApi({
  reducerPath: 'inspectionApi',
  refetchOnReconnect: true,
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootStates).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    createInspection: build.mutation({
      query: (body) => ({
        url: `${apiUrl.project}/${body.id}/inspections`,
        method: 'POST',
        body: body,
      }),
    }),
    updateInspectionPatch: build.mutation({
      query: (params) => ({
        url: `${apiUrl.project}/${params.projectId}/inspections/${params.inspectionId}/${params.page}`,
        method: 'PATCH',
        body: params.body,
      }),
    }),
    scheduleInspection: build.mutation({
      query: (body) => ({
        url: `${apiUrl.project}/${body.id}/inspections/schedule`,
        method: 'POST',
        body: body,
      }),
    }),
    getAllInspections: build.mutation({
      query: (id) => ({
        url: `${apiUrl.project}/${id}/inspections`,
        method: 'get',
      }),
    }),
    getPaginatedInspections: build.mutation({
      query: ({
        id,
        page = 1,
        limit = 16,
        status = '',
        filterBy = '',
        filterValue = '',
      }) => ({
        url: `${apiUrl.project}/${id}/inspections?filterBy=${filterBy}&filterValue=${filterValue}&status=${status}&page=${page}&limit=${limit}`,
        method: 'get',
      }),
    }),
    getInspectionById: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.projectId}/inspections/records/${param.id}`,
        method: 'get',
      }),
    }),
    getInspectionsByStatus: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.projectId}/inspections?status=${param.status}`,
        method: 'get',
      }),
    }),
    getLocations: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.id}/inspections/locations`,
        method: 'get',
      }),
    }),
    getCounts: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.id}/inspections/counts`,
        method: 'get',
      }),
    }),
    rescheduleInspection: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.projectId}/inspections/${param.id}/operations/RESCHEDULE`,
        method: 'PATCH',
        body: param.body,
      }),
    }),
    cancelInspection: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.projectId}/inspections/${param.id}/operations/CANCEL`,
        method: 'PATCH',
        body: param.body,
      }),
    }),
    getTasksByType: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.projectId}/tasks?taskType=INSPECTION&taskTypeId=${param.id}&status=ALL`,
        method: 'get',
        body: param.body,
      }),
    }),
    addComment: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.projectId}/inspections/${param.id}/questions/${param.qId}`,
        method: 'PATCH',
        body: param.body,
      }),
    }),
    getReport: build.mutation({
      query: (param) => ({
        url: `/inspections/${param.id}/reports`,
        method: 'get',
      }),
    }),
  }),
});

export const {
  useCreateInspectionMutation,
  useScheduleInspectionMutation,
  useGetAllInspectionsMutation,
  useGetInspectionsByStatusMutation,
  useUpdateInspectionPatchMutation,
  useGetPaginatedInspectionsMutation,
  useGetLocationsMutation,
  useGetCountsMutation,
  useCancelInspectionMutation,
  useRescheduleInspectionMutation,
  useGetInspectionByIdMutation,
  useGetTasksByTypeMutation,
  useAddCommentMutation,
  useGetReportMutation,
} = inspectionApi;
